import { useCallback, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { Tabs as TabsComponent, TabsProps } from '@/components/chariot-ui';
import { HOTKEY_SCOPES } from '@/hooks/hotkeys';

const Tabs = (
  props: TabsProps & {
    id: string;
    keyboardDependencies?: string[];
    keyboardNavigation?: boolean;
  }
) => {
  const { tabs, selected, onSelect, vertical, ...rest } = props;
  const { keyboardDependencies = [], keyboardNavigation = true } = rest;

  // Direct handle on the wrapper DOM node so we can move focus to the newly
  // selected tab trigger after navigation (see focusTabTrigger below).
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Focus-follows-selection: after onSelect changes the active tab, move DOM
  // focus to that tab's trigger so focus stays pinned INSIDE the wrapper. The
  // element-ref gate on `useHotkeys` only fires while activeElement is within
  // the wrapper; selecting a tab can drift focus to the panel's content (a
  // focusable div), leaving the wrapper and breaking the next arrow press.
  // Triggers exist regardless of selection (only `data-state` changes) and
  // their DOM order matches the `tabs` array order, so indexing by tab index
  // is safe. The refocus is deferred to the next macrotask so it runs AFTER the
  // newly-selected panel's post-commit auto-focus (which would otherwise
  // immediately overwrite a synchronous refocus and drift focus out of the
  // wrapper, killing the next arrow press).
  function focusTabTrigger(index: number) {
    window.setTimeout(() => {
      const triggers =
        wrapperRef.current?.querySelectorAll<HTMLElement>('[role="tab"]');
      triggers?.[index]?.focus();
    }, 0);
  }

  function handleNext(event: KeyboardEvent) {
    event.preventDefault();
    const foundIndex = tabs.findIndex(tab => tab.value === selected?.value);
    const currentTabIndex = foundIndex === -1 ? 0 : foundIndex;

    for (let i = 1; i <= tabs.length; i++) {
      const candidateIndex = (currentTabIndex + i) % tabs.length;
      const candidate = tabs[candidateIndex];
      if (candidate && !candidate.disabled) {
        onSelect(candidate);
        focusTabTrigger(candidateIndex);
        return;
      }
    }
  }

  function handlePrevious(event: KeyboardEvent) {
    event.preventDefault();
    const foundIndex = tabs.findIndex(tab => tab.value === selected?.value);
    const currentTabIndex = foundIndex === -1 ? 0 : foundIndex;

    for (let i = 1; i <= tabs.length; i++) {
      const candidateIndex = (currentTabIndex - i + tabs.length) % tabs.length;
      const candidate = tabs[candidateIndex];
      if (candidate && !candidate.disabled) {
        onSelect(candidate);
        focusTabTrigger(candidateIndex);
        return;
      }
    }
  }

  // Ref-stabilize the keydown dispatch so it always sees the latest `onSelect`
  // and `tabs`/`selected` (handleNext/handlePrevious close over all three). rhk
  // v5 FREEZES the callback whenever a deps array is passed — the old
  // `keyboardDependencies`-based deps could omit a changed `onSelect` or
  // refreshed tab objects, leaving arrow-nav calling a stale handler. Updating
  // the ref every render and passing NO deps to `useHotkeys` makes the latest
  // closure always run, so `keyboardDependencies` is no longer load-bearing for
  // freshness (kept in the signature for back-compat).
  const dispatchRef = useRef<(event: KeyboardEvent) => void>(() => {});
  // Latest-ref pattern: writing `.current` during render keeps the dispatch
  // pointed at the freshest onSelect/tabs closure; it is read only later inside
  // the keydown handler, never during this render.
  // eslint-disable-next-line react-hooks/refs
  dispatchRef.current = (event: KeyboardEvent) => {
    const key = event.key;
    if (key === 'ArrowDown' || key === 'ArrowRight') {
      handleNext(event);
    } else if (key === 'ArrowUp' || key === 'ArrowLeft') {
      handlePrevious(event);
    }
  };
  // `keyboardDependencies` is retained in the props for back-compat but is no
  // longer load-bearing now that the handler is ref-stabilized.
  void keyboardDependencies;

  // Element-scoped tab navigation. `useHotkeys` returns a RefCallback that, when
  // attached to a DOM node, gates the binding to keydowns whose target is inside
  // that node. We attach it to the wrapper below so arrows only move tabs when
  // focus is within THIS Tabs instance — multiple Tabs on a page (or in a drawer)
  // don't all respond to the same arrow press. Scope is `overlay` rather than
  // `global` because Tabs can live inside drawers, and `global` is suppressed
  // whenever an overlay is open; `overlay` is never globally disabled, so tab
  // navigation keeps working inside a drawer. The focus-within element gate is
  // what actually prevents cross-instance collisions.
  // `enabled` mirrors the original `keyboardNavigation` gate; `tabs.length > 0`
  // is a safe extra guard since the handlers index into `tabs`.
  // No deps passed: rhk refreshes its internal callback ref every render, and
  // the wrapper always invokes the latest `dispatchRef.current`.
  const hotkeyRef = useHotkeys<HTMLDivElement>(
    vertical ? ['up', 'down'] : ['left', 'right'],
    event => dispatchRef.current(event),
    {
      enabled: keyboardNavigation && tabs.length > 0,
      scopes: [HOTKEY_SCOPES.overlay],
      enableOnFormTags: false,
      preventDefault: true,
    }
  );

  // Merge the RefCallback from useHotkeys with our own wrapperRef so we can both
  // gate the hotkey binding (hotkeyRef) and read the wrapper node directly
  // (wrapperRef) to move focus after navigation. useCallback keeps the
  // RefCallback identity stable so React doesn't detach/reattach the ref.
  const setWrapperRef = useCallback(
    (node: HTMLDivElement | null) => {
      hotkeyRef(node);
      wrapperRef.current = node;
    },
    [hotkeyRef]
  );

  return (
    <div ref={setWrapperRef}>
      <TabsComponent {...props} />
    </div>
  );
};

export default Tabs;
